import React, { useState } from 'react';
import ImageUploader from '../common/ImageUploader';
import RichTextEditor from '../common/RichTextEditor';
import { Tag, Eye, EyeOff } from 'lucide-react';

export const slugify = (str) =>
  (str || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const BlogMainTab = ({ formData, setFormData, onSlugEdit }) => {
  const [tagInput, setTagInput] = useState('');

  const addHashtag = () => {
    const tag = tagInput.trim().replace(/^#/, '');
    if (tag && !formData.hashtags.includes(tag)) {
      setFormData((prev) => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
    }
    setTagInput('');
  };

  const removeHashtag = (tag) => {
    setFormData((prev) => ({ ...prev, hashtags: prev.hashtags.filter((t) => t !== tag) }));
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">Title *</label>
        <input
          type="text"
          required
          data-testid="blog-title-input"
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
          placeholder="Enter your blog title..."
        />
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">Custom URL Slug *</label>
        <div className="flex items-stretch border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
          <span className="px-3 py-3 bg-gray-50 text-gray-500 text-sm border-r border-gray-300 select-none">/blog/</span>
          <input
            type="text"
            required
            data-testid="blog-slug-input"
            value={formData.slug}
            onChange={(e) => {
              onSlugEdit();
              setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }));
            }}
            className="flex-1 px-4 py-3 focus:outline-none"
            placeholder="my-awesome-post"
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">Lowercase letters, numbers, and hyphens only. Auto-suggested from the title.</p>
      </div>

      {/* Featured Image */}
      <div>
        <ImageUploader
          label="Featured Image *"
          value={formData.featured_image}
          onChange={(url) => setFormData((prev) => ({ ...prev, featured_image: url }))}
          placeholder="Upload or paste a URL for the featured image"
        />
        <p className="mt-1.5 text-xs text-gray-500">Recommended size: 1920 × 1080px (16:9) for the sharpest, uncropped display on the blog page.</p>
      </div>

      {/* Category & Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">Category *</label>
          <input
            type="text"
            required
            data-testid="blog-category-input"
            value={formData.category}
            onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g. Technology"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="blog-status-published-btn"
              onClick={() => setFormData((prev) => ({ ...prev, status: 'published' }))}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                formData.status === 'published' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Eye size={16} className="inline mr-2" />Published
            </button>
            <button
              type="button"
              data-testid="blog-status-draft-btn"
              onClick={() => setFormData((prev) => ({ ...prev, status: 'draft' }))}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                formData.status === 'draft' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <EyeOff size={16} className="inline mr-2" />Draft
            </button>
          </div>
        </div>
      </div>

      {/* Hashtags */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          <Tag size={16} className="inline mr-2" />Hashtags
        </label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            data-testid="blog-hashtag-input"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Add a hashtag and press Enter..."
          />
          <button
            type="button"
            data-testid="blog-hashtag-add-btn"
            onClick={addHashtag}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.hashtags.map((tag) => (
            <span key={tag} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center">
              #{tag}
              <button type="button" data-testid={`blog-hashtag-remove-${tag}`} onClick={() => removeHashtag(tag)} className="ml-2 text-blue-500 hover:text-blue-700">×</button>
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">Content *</label>
        <RichTextEditor
          content={formData.content}
          onChange={(content) => setFormData((prev) => ({ ...prev, content }))}
          placeholder="Start writing your blog post..."
        />
      </div>
    </div>
  );
};

export default BlogMainTab;
